/** longest side, in px, the rasterized logo should reach */
const TARGET_LOGO_PX = 1000;
/** bounds accepted by the media upload endpoint */
const MIN_DPI = 72;
const MAX_DPI = 720;
const BASE_DPI = 72;

const PX_PER_UNIT: { [unit: string]: number } = {
  '': 1,
  px: 1,
  pt: 4 / 3,
  pc: 16,
  mm: 96 / 25.4,
  cm: 96 / 2.54,
  in: 96,
};

/** converts an absolute svg length to px; null for relative units (%, em, ...) or garbage */
function lengthToPx(value: string | null): number | null {
  const match = value?.trim().match(/^([+]?\d*\.?\d+(?:e[+-]?\d+)?)([a-z]*)$/i);
  const factor = match ? PX_PER_UNIT[match[2].toLowerCase()] : undefined;
  if (!match || factor === undefined) return null;

  const px = parseFloat(match[1]) * factor;
  return px > 0 ? px : null;
}

/** intrinsic size of the svg: width/height when absolute, otherwise the viewBox */
function intrinsicSize(svg: Element): { width: number; height: number } | null {
  const width = lengthToPx(svg.getAttribute('width'));
  const height = lengthToPx(svg.getAttribute('height'));
  if (width && height) return { width, height };

  const viewBox = svg
    .getAttribute('viewBox')
    ?.trim()
    .split(/[\s,]+/)
    .map(Number);
  if (viewBox?.length === 4 && viewBox[2] > 0 && viewBox[3] > 0) return { width: viewBox[2], height: viewBox[3] };

  return null;
}

/** pixel density that rasterizes the svg to the target size; undefined when its size can't be determined */
export function detectSvgDensity(markup: string): number | undefined {
  const root = new DOMParser().parseFromString(markup, 'image/svg+xml').documentElement;
  const size = intrinsicSize(root);
  if (!size) return undefined;

  const dpi = Math.round((BASE_DPI * TARGET_LOGO_PX) / Math.max(size.width, size.height));
  return Math.min(Math.max(dpi, MIN_DPI), MAX_DPI);
}
